import { type Menus_Bool_Exp, useGetMenusQuery } from '@/__generated__/graphql';
import type { TablePaginationConfig } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Menu } from '../types';

export const useMenuTable = () => {
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50'],
  });

  const variables = useMemo(
    () => ({
      limit: pagination.pageSize || 10,
      offset: ((pagination.current || 1) - 1) * (pagination.pageSize || 10),
      where: searchText
        ? ({ name: { _ilike: `%${searchText}%` } } as Menus_Bool_Exp)
        : ({} as Menus_Bool_Exp),
    }),
    [pagination, searchText],
  );

  const { loading, data, refetch } = useGetMenusQuery({
    variables,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const totalCount = data?.menus_aggregate?.aggregate?.count;
    if (totalCount !== undefined && pagination.total !== totalCount) {
      setPagination((prev) => ({
        ...prev,
        total: totalCount,
      }));
    }
  }, [data?.menus_aggregate?.aggregate?.count, pagination.total]);

  const handleTableChange = useCallback(
    (newPagination: TablePaginationConfig) => {
      setPagination(newPagination);
    },
    [],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchText(value);
      setPagination({
        ...pagination,
        current: 1,
      });
    },
    [pagination],
  );

  return {
    searchText,
    pagination,
    loading,
    data: (data?.menus ?? []) as Menu[],
    refetch,
    handleTableChange,
    handleSearch,
  };
};
